import { useState, useEffect, useRef } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { trpc, toastTrpcError } from "@/lib/trpc";
import {
  EntityNotFound,
  EntityErrorState,
} from "@/components/layout/entity-state";
import { isValidUuid } from "@/lib/utils";
import { useDebounce } from "@/hooks/use-debounce";
import { authedFetch } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Page } from "@/components/layout/page";
import { ActionBar } from "@/components/layout/action-bar";
import { DangerZone } from "@/components/layout/danger-zone";
import { CanEdit, useCanEdit } from "@/lib/project-access";
import { statusDotColors, statusLabels } from "@/components/source-status";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AriaTabs,
  AriaTabList,
  AriaTab,
  AriaTabPanel,
} from "@/components/ui/aria-tabs";
import { PillTrigger } from "@/components/ui/pill-trigger";
import { Skeleton } from "@/components/ui/skeleton";
import { AuthorityBadge } from "@/components/authority-badge";
import {
  parseCronSchedule,
  CRON_PRESETS,
  getCronLabel,
  authorityConfig,
  authorityLevelsSorted,
  type AuthorityLevel,
} from "@iamu/shared";
import { computeNextDue, formatRelativeTime } from "@/lib/freshness";
import { Timestamp } from "@/components/ui/timestamp";
import { STALE_TIMES } from "@/lib/query-performance";
import { ProcessingStepper } from "@/components/processing-stepper";
import { InlineWarning } from "@/components/inline-warning";
import { toast } from "sonner";
import {
  Globe,
  FileText,
  PenLine,
  ExternalLink,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Layers,
  History,
  Settings,
  BookOpenText,
  RefreshCw,
  Loader2,
  Palette,
  Search,
  X,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL = 3000;
const POLL_TIMEOUT = 120_000;

const sourceTypeIcons = {
  web: Globe,
  document: FileText,
  manual: PenLine,
} as const;

// ProcessingStepper extracted to @/components/processing-stepper

// ---------------------------------------------------------------------------
// Freshness Dot
// ---------------------------------------------------------------------------

function FreshnessDot({
  crawlSchedule,
  lastCrawledAt,
}: {
  crawlSchedule: string | null;
  lastCrawledAt: string | null;
}) {
  if (!crawlSchedule || !lastCrawledAt) return null;

  const next = computeNextDue(crawlSchedule, lastCrawledAt);
  if (!next) return null;

  const now = Date.now();
  const nextMs = next.getTime();
  const lastMs = new Date(lastCrawledAt).getTime();
  const windowMs = nextMs - lastMs;
  const elapsed = now - lastMs;
  const ratio = windowMs > 0 ? elapsed / windowMs : 0;

  let color = "bg-mark-helps";
  let label = "Freshness: up to date";
  if (ratio > 1) {
    color = "bg-mark-critical";
    label = "Freshness: overdue for recrawl";
  } else if (ratio > 0.8) {
    color = "bg-mark-important";
    label = "Freshness: recrawl due soon";
  }

  return (
    <span
      className={`inline-block size-2 rounded-full ${color}`}
      role="img"
      aria-label={label}
    />
  );
}

// ---------------------------------------------------------------------------
// Markdown Renderer (shared between Content tab and Chunk explorer)
// ---------------------------------------------------------------------------

function MarkdownContent({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  return (
    <div
      className={`prose prose-sm dark:prose-invert max-w-none ${className ?? ""}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible Chunk Card
// ---------------------------------------------------------------------------

function ChunkCard({
  chunk,
  isExpanded,
  onToggle,
  searchTerm,
}: {
  chunk: {
    id: string;
    content: string;
    contextHeader: string | null;
    contextText: string | null;
    chunkType: string;
    chunkIndex: number | null;
    language: string | null;
    embeddingModel: string | null;
    chunkingStrategy: string | null;
  };
  isExpanded: boolean;
  onToggle: () => void;
  searchTerm: string;
}) {
  const previewText =
    chunk.content.slice(0, 200) + (chunk.content.length > 200 ? "..." : "");

  // Extract a readable heading from contextHeader — strip the source title prefix
  const heading = chunk.contextHeader?.split(">").pop()?.trim() || null;

  function highlightMarkdown(text: string) {
    if (!searchTerm) return text;
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escaped})`, "gi"));
    return parts
      .map((part) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? `**${part}**` : part,
      )
      .join("");
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          {heading && (
            <div className="text-sm font-semibold tracking-tight truncate">
              {heading}
            </div>
          )}
          {!isExpanded && (
            <p
              className={`text-sm text-muted-foreground line-clamp-2 ${heading ? "mt-1" : ""}`}
            >
              {previewText}
            </p>
          )}
        </div>
        <span className="text-xs text-muted-foreground shrink-0 mt-0.5">
          {chunk.chunkType}
        </span>
      </button>
      {isExpanded && (
        <div className="px-5 pb-5 pt-0 ml-7">
          {chunk.contextText && (
            <div className="mb-3 rounded-lg bg-primary/5 px-4 py-3">
              <p className="text-sm text-foreground leading-relaxed">
                {chunk.contextText}
              </p>
            </div>
          )}
          <div className="max-w-prose">
            <MarkdownContent
              content={
                searchTerm ? highlightMarkdown(chunk.content) : chunk.content
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Content Tab
// ---------------------------------------------------------------------------

function ContentTab({
  sourceVersionId,
  sourceId,
  url,
}: {
  sourceVersionId: string | undefined;
  sourceId: string | undefined;
  url: string | null;
}) {
  const queryInput = sourceVersionId
    ? { sourceVersionId }
    : sourceId
      ? { sourceId }
      : null;
  const { data, isLoading } = trpc.source.getContent.useQuery(queryInput!, {
    enabled: !!queryInput,
  });

  if (!queryInput) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <BookOpenText className="size-12 text-muted-foreground mb-3" />
        <p className="text-muted-foreground">No content available yet.</p>
        <p className="text-sm text-muted-foreground mt-1">
          Content will appear here once the source has been processed.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="px-10 py-8 space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.content) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <BookOpenText className="size-12 text-muted-foreground mb-3" />
        <p className="text-muted-foreground">
          No content found for this version.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-2">
        <Button
          variant="ghost"
          size="xs"
          className="text-muted-foreground"
          onClick={() => {
            navigator.clipboard.writeText(data.content);
            toast.success("Copied");
          }}
        >
          <Copy className="size-3" />
          Copy
        </Button>
      </div>
      <Card>
        <CardContent className="px-4 py-4 sm:px-10 sm:py-8 overflow-x-auto">
          {data.truncated && (
            <p className="text-xs text-muted-foreground mb-4">
              Truncated — showing first 200k characters
            </p>
          )}
          <MarkdownContent content={data.content} />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Knowledge Tab (Chunk Explorer)
// ---------------------------------------------------------------------------

function KnowledgeTab({
  sourceVersionId,
  sourceId,
  pageUrl,
  onClearPageUrl,
}: {
  sourceVersionId: string | undefined;
  sourceId: string | undefined;
  pageUrl?: string;
  onClearPageUrl?: () => void;
}) {
  const [chunkOffset, setChunkOffset] = useState(0);
  const [chunkSearchInput, setChunkSearchInput] = useState("");
  const debouncedChunkSearch = useDebounce(chunkSearchInput, 300);
  const [typeFilter, setTypeFilter] = useState<"all" | "parent" | "child">(
    "all",
  );
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set());
  const chunkLimit = 20;

  // Reset offset when search changes
  useEffect(() => {
    setChunkOffset(0);
  }, [debouncedChunkSearch]);

  // Reset offset when page filter changes
  const prevPageUrl = useRef(pageUrl);
  useEffect(() => {
    if (prevPageUrl.current !== pageUrl) {
      setChunkOffset(0);
      prevPageUrl.current = pageUrl;
    }
  }, [pageUrl]);

  const chunkQueryBase = sourceVersionId
    ? { sourceVersionId }
    : sourceId
      ? { sourceId }
      : null;
  const { data: chunksData, isLoading: chunksLoading } =
    trpc.source.getChunks.useQuery(
      {
        ...chunkQueryBase!,
        limit: chunkLimit,
        offset: chunkOffset,
        search: debouncedChunkSearch.trim() || undefined,
        chunkType: typeFilter === "all" ? undefined : typeFilter,
        pageUrl,
      },
      { enabled: !!chunkQueryBase },
    );

  function toggleChunk(id: string) {
    setExpandedChunks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function expandAll() {
    if (!chunksData) return;
    setExpandedChunks(new Set(chunksData.chunks.map((c) => c.id)));
  }

  function collapseAll() {
    setExpandedChunks(new Set());
  }

  if (!chunkQueryBase) {
    return (
      <p className="text-muted-foreground py-8 text-center">
        No version available.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Active page filter */}
      {pageUrl && (
        <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2">
          <Globe className="size-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm truncate max-w-md">{pageUrl}</span>
          <button
            type="button"
            onClick={onClearPageUrl}
            className="text-muted-foreground hover:text-foreground shrink-0"
            aria-label="Clear page filter"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search chunks..."
            value={chunkSearchInput}
            onChange={(e) => setChunkSearchInput(e.target.value)}
            className="h-9 w-52 pl-8 text-sm"
            aria-label="Search chunks"
          />
          {chunkSearchInput && (
            <button
              type="button"
              onClick={() => {
                setChunkSearchInput("");
                setChunkOffset(0);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        <Select
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v as "all" | "parent" | "child");
            setChunkOffset(0);
          }}
        >
          <SelectTrigger className="h-9 w-auto min-w-[100px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All chunks</SelectItem>
            <SelectItem value="parent">Parent</SelectItem>
            <SelectItem value="child">Child</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={expandAll}
          >
            Expand all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={collapseAll}
          >
            Collapse all
          </Button>
        </div>
      </div>

      {/* Chunk list */}
      {chunksLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="bg-card rounded-lg px-5 py-4 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
            >
              <Skeleton className="h-4 w-2/5 mb-2" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      ) : !chunksData?.chunks.length ? (
        <p className="text-muted-foreground py-8 text-center">
          {debouncedChunkSearch
            ? `No chunks matching "${debouncedChunkSearch}".`
            : "No chunks for this version."}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            {chunksData.chunks.map((chunk) => (
              <ChunkCard
                key={chunk.id}
                chunk={chunk}
                isExpanded={expandedChunks.has(chunk.id)}
                onToggle={() => toggleChunk(chunk.id)}
                searchTerm={debouncedChunkSearch}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              size="sm"
              disabled={chunkOffset === 0}
              onClick={() =>
                setChunkOffset(Math.max(0, chunkOffset - chunkLimit))
              }
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              {chunkOffset + 1}–
              {Math.min(chunkOffset + chunkLimit, chunksData.totalCount)} of{" "}
              {chunksData.totalCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={chunkOffset + chunkLimit >= chunksData.totalCount}
              onClick={() => setChunkOffset(chunkOffset + chunkLimit)}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pages Tab (bge crawler sources)
// ---------------------------------------------------------------------------

function PagesTab({
  sourceVersionId,
  sourceId,
  onSelectPage,
}: {
  sourceVersionId: string | undefined;
  sourceId: string | undefined;
  onSelectPage: (url: string) => void;
}) {
  const [offset, setOffset] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const debouncedPageSearch = useDebounce(searchInput, 300);
  const limit = 50;

  useEffect(() => {
    setOffset(0);
  }, [debouncedPageSearch]);

  const pageQueryBase = sourceVersionId
    ? { sourceVersionId }
    : sourceId
      ? { sourceId }
      : null;
  const { data, isLoading } = trpc.source.getPages.useQuery(
    {
      ...pageQueryBase!,
      limit,
      offset,
      search: debouncedPageSearch.trim() || undefined,
    },
    { enabled: !!pageQueryBase },
  );

  if (!pageQueryBase) {
    return (
      <p className="text-muted-foreground py-8 text-center">
        No data available.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
        <Input
          placeholder="Search pages..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="h-9 w-64 pl-8 text-sm"
          aria-label="Search pages"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => {
              setSearchInput("");
              setOffset(0);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {/* Page list */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="bg-card rounded-lg px-5 py-4 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
            >
              <Skeleton className="h-4 w-2/5 mb-2" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      ) : !data?.pages.length ? (
        <p className="text-muted-foreground py-8 text-center">
          {debouncedPageSearch
            ? `No pages matching "${debouncedPageSearch}".`
            : "No pages found."}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            {data.pages.map((page) => (
              <button
                key={page.url}
                type="button"
                onClick={() => onSelectPage(page.url)}
                className="bg-card flex w-full items-center gap-4 rounded-lg border border-border px-4 py-3 text-left hover:bg-muted/40 transition-colors"
              >
                <Globe className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold tracking-tight truncate">
                    {page.title || page.url}
                  </div>
                  {page.title && (
                    <p className="text-sm text-muted-foreground truncate mt-0.5">
                      {page.url}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-muted-foreground">
                    {page.chunkCount} chunks
                  </span>
                  {page.crawledAt && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(page.crawledAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {data.totalCount > limit && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                {offset + 1}–{Math.min(offset + limit, data.totalCount)} of{" "}
                {data.totalCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + limit >= data.totalCount}
                onClick={() => setOffset(offset + limit)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Content Search Tab (crawler semantic search)
// ---------------------------------------------------------------------------

function ContentSearchTab({ projectId }: { projectId: string }) {
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");

  const { data, isLoading } = trpc.source.searchContent.useQuery(
    { projectId, query, limit: 10 },
    { enabled: !!query },
  );

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = searchInput.trim();
          if (trimmed) setQuery(trimmed);
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search ingested content..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-9 pl-8 text-sm"
            aria-label="Search content"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setQuery("");
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <Button type="submit" size="sm" disabled={!searchInput.trim()}>
          Search
        </Button>
      </form>

      {!query && (
        <p className="text-muted-foreground py-8 text-center text-sm">
          Search across all ingested content using semantic search.
        </p>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="bg-card rounded-lg px-5 py-4 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
            >
              <Skeleton className="h-4 w-2/5 mb-2" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4 mt-1" />
            </div>
          ))}
        </div>
      )}

      {query && !isLoading && (!data || data.length === 0) && (
        <p className="text-muted-foreground py-8 text-center text-sm">
          No results for &ldquo;{query}&rdquo;.
        </p>
      )}

      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((hit) => (
            <div
              key={hit.id}
              className="bg-card rounded-lg px-5 py-4 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
            >
              <div className="flex items-center gap-2 mb-1">
                {hit.section && (
                  <span className="text-sm font-semibold tracking-tight truncate">
                    {hit.section}
                  </span>
                )}
                <span className="text-xs text-muted-foreground ml-auto shrink-0">
                  {Math.round(hit.score * 100)}% match
                </span>
              </div>
              <p className="text-sm text-foreground line-clamp-3">
                {hit.content}
              </p>
              <p className="text-xs text-muted-foreground mt-1.5 truncate">
                {hit.sourceName}
                {hit.pageUrl !== hit.sourceName && ` — ${hit.pageUrl}`}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History Tab
// ---------------------------------------------------------------------------

function HistoryTab({
  versions,
}: {
  versions: Array<{
    id: string;
    versionNumber: number;
    effectiveFrom: string;
    effectiveUntil: string | null;
    contentHash: string | null;
    embeddingModel: string | null;
  }>;
}) {
  if (!versions.length) {
    return (
      <p className="text-muted-foreground py-8 text-center">No versions yet.</p>
    );
  }

  return (
    <div className="relative ml-3 border-l border-border pl-6">
      {[...versions].reverse().map((v) => {
        const isCurrent = !v.effectiveUntil;
        return (
          <div key={v.id} className="relative pb-6 last:pb-0">
            <div
              className={`absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 ${
                isCurrent
                  ? "border-mark-helps bg-mark-helps"
                  : "border-muted-foreground bg-background"
              }`}
            />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium">
                  v{v.versionNumber}
                  {isCurrent && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Current
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  <Timestamp date={v.effectiveFrom} />
                  {v.effectiveUntil && (
                    <>
                      {" "}
                      &mdash; <Timestamp date={v.effectiveUntil} />
                    </>
                  )}
                </p>
              </div>
              <details className="text-right text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none">
                  Technical
                </summary>
                <p className="font-mono mt-1">
                  {v.contentHash?.slice(0, 12)}...
                </p>
                <p>{v.embeddingModel ?? "—"}</p>
              </details>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings Tab
// ---------------------------------------------------------------------------

function SettingsTab({
  data,
  onUpdate,
  onDelete,
  isDeleting,
}: {
  data: {
    name: string;
    authorityLevel: string;
    language: string | null;
    crawlSchedule: string | null;
    sourceType: string;
  };
  onUpdate: (values: {
    name: string;
    authorityLevel: AuthorityLevel;
    language: string | null;
    crawlSchedule: string | null;
  }) => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const [editName, setEditName] = useState(data.name);
  const [editAuthority, setEditAuthority] = useState(data.authorityLevel);
  const [editLanguage, setEditLanguage] = useState(data.language ?? "");
  const [editCrawlSchedule, setEditCrawlSchedule] = useState(
    data.crawlSchedule ?? "",
  );
  const [cronError, setCronError] = useState("");

  function validateCron(value: string) {
    if (!value) {
      setCronError("");
      return;
    }
    try {
      parseCronSchedule(value);
      setCronError("");
    } catch {
      setCronError(`Invalid cron: ${value}`);
    }
  }

  function save() {
    if (!editName.trim() || cronError) return;
    onUpdate({
      name: editName,
      authorityLevel: editAuthority as AuthorityLevel,
      language: editLanguage || null,
      crawlSchedule: editCrawlSchedule || null,
    });
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
          {!editName.trim() && (
            <p className="text-sm text-destructive">Name is required</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="source-authority">Authority level</Label>
          <Select value={editAuthority} onValueChange={setEditAuthority}>
            <SelectTrigger id="source-authority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {authorityLevelsSorted.map((a) => (
                <SelectItem key={a} value={a}>
                  {authorityConfig[a].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="language">
            Language{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            id="language"
            value={editLanguage}
            onChange={(e) => setEditLanguage(e.target.value)}
            placeholder="e.g. english, swedish"
          />
        </div>
        {data.sourceType === "web" && (
          <div className="space-y-2">
            <Label htmlFor="crawlSchedule">
              Crawl Schedule{" "}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </Label>
            <div className="flex flex-wrap gap-1 mb-1">
              {CRON_PRESETS.map((preset) => (
                <Button
                  key={preset.cron}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-sm"
                  onClick={() => {
                    setEditCrawlSchedule(preset.cron);
                    setCronError("");
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <Input
              id="crawlSchedule"
              value={editCrawlSchedule}
              onChange={(e) => setEditCrawlSchedule(e.target.value)}
              onBlur={(e) => validateCron(e.target.value)}
              placeholder="e.g. 0 0 * * *"
            />
            {cronError && (
              <p className="text-sm text-destructive">{cronError}</p>
            )}
          </div>
        )}
        <Button onClick={save} disabled={!editName.trim() || !!cronError}>
          Save changes
        </Button>
      </div>

      <DangerZone
        title="Delete source"
        description={`Permanently delete “${data.name}” and all its versions and chunks. This cannot be undone.`}
        actionLabel="Delete source"
        confirmTitle="Delete this source?"
        confirmDescription={`This will permanently delete “${data.name}” and all its versions and chunks.`}
        onConfirm={onDelete}
        isPending={isDeleting}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parse Quality Info
// ---------------------------------------------------------------------------

function ParseQualityInfo({ metadata }: { metadata: Record<string, unknown> }) {
  const pq = metadata as {
    parserUsed?: string;
    elementCounts?: Record<string, number>;
    pageCount?: number | null;
    processingTimeSeconds?: number | null;
    warnings?: string[];
  };
  const isFallback = pq.parserUsed?.startsWith("plaintext");

  return (
    <div className="space-y-2">
      {isFallback && (
        <InlineWarning>
          This document was parsed as plain text — structural information
          (headings, tables) was not extracted.
        </InlineWarning>
      )}
      {pq.warnings?.filter(Boolean).map((w, i) => (
        <InlineWarning key={i} size="sm" icon={false}>
          {w}
        </InlineWarning>
      ))}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        {pq.parserUsed && (
          <span>
            Parser: <span className="font-mono">{pq.parserUsed}</span>
          </span>
        )}
        {typeof pq.pageCount === "number" && <span>{pq.pageCount} pages</span>}
        {pq.elementCounts && Object.keys(pq.elementCounts).length > 0 && (
          <span>
            {Object.entries(pq.elementCounts)
              .map(
                ([type, count]) => `${count} ${type}${count !== 1 ? "s" : ""}`,
              )
              .join(", ")}
          </span>
        )}
        {typeof pq.processingTimeSeconds === "number" && (
          <span>{pq.processingTimeSeconds.toFixed(1)}s parse time</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function SourceDetailPage() {
  const params = useParams<{ id: string; sourceId: string }>();
  const projectId = params.id;
  const sourceId = params.sourceId ?? params.id;
  const id = sourceId;
  const isValidId = isValidUuid(sourceId);
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "content";
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data, isLoading, error, refetch } = trpc.source.getById.useQuery(
    { id: sourceId! },
    { enabled: isValidId, staleTime: STALE_TIMES.detail },
  );

  const canEdit = useCanEdit();
  const updateMutation = trpc.source.update.useMutation({
    onSuccess: () => {
      utils.source.getById.invalidate({ id: id! });
      toast.success("Source updated");
    },
    onError: (err) => toastTrpcError(err, "Failed to update source"),
  });

  const deleteMutation = trpc.source.delete.useMutation({
    onSuccess: () => {
      toast.success("Source deleted");
      navigate(params.sourceId ? `/projects/${params.id}/sources` : "/sources");
    },
    onError: (err) => toastTrpcError(err, "Failed to delete source"),
  });

  const recrawlMutation = trpc.source.recrawl.useMutation({
    onSuccess: () => utils.source.getById.invalidate({ id: id! }),
  });

  const [crawlProgress, setCrawlProgress] = useState<{
    stage: string;
    progress: number;
    currentUrl?: string;
    currentTitle?: string;
    totalProcessed?: number;
    chunksCreated?: number;
  } | null>(null);
  const [isCrawling, setIsCrawling] = useState(false);

  async function handleRetryCrawl() {
    if (!id || isCrawling) return;
    setIsCrawling(true);
    setCrawlProgress(null);
    const toastId = toast.loading("Starting crawl...");

    try {
      const res = await authedFetch(`/api/sources/${id}/recrawl`, {
        method: "POST",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error((body as { error?: string }).error ?? "Recrawl failed");
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const { readSSEStream } = await import("@/lib/sse-reader");
      await readSSEStream<Record<string, unknown>>(reader, (data, event) => {
        switch (event) {
          case "progress": {
            const p = data as typeof crawlProgress;
            setCrawlProgress(p);
            const label = p?.currentTitle || p?.currentUrl || "";
            toast.loading(
              `Crawling — ${p?.progress ?? 0}%${label ? ` · ${label}` : ""}`,
              { id: toastId },
            );
            break;
          }
          case "done":
            toast.success("Crawl complete", { id: toastId });
            break;
          case "error":
            toast.error(
              String((data as { message?: string }).message ?? "Crawl failed"),
              { id: toastId },
            );
            return false;
        }
      });

      utils.source.getById.invalidate({ id: id! });
      utils.source.getChunks.invalidate();
      utils.source.getPages.invalidate();
    } catch (err) {
      toastTrpcError(err, "Failed to retry");
    } finally {
      setIsCrawling(false);
      setCrawlProgress(null);
    }
  }

  // Polling for processing status
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const pollingStart = useRef<number | null>(null);

  useEffect(() => {
    if (data?.status !== "processing") {
      pollingStart.current = null;
      setPollingTimedOut(false);
      return;
    }
    if (pollingTimedOut) return;
    if (!pollingStart.current) pollingStart.current = Date.now();

    const timer = setInterval(() => {
      if (
        pollingStart.current &&
        Date.now() - pollingStart.current > POLL_TIMEOUT
      ) {
        setPollingTimedOut(true);
        clearInterval(timer);
        return;
      }
      utils.source.getById.invalidate({ id: id! });
    }, POLL_INTERVAL);

    return () => clearInterval(timer);
  }, [data?.status, pollingTimedOut, id, utils]);

  // Verification
  const [verifying, setVerifying] = useState(false);
  const [verifyAuthority, setVerifyAuthority] = useState("");

  async function handleVerify(authorityOverride?: string) {
    setVerifying(true);
    try {
      const body: Record<string, unknown> = { verified: true };
      if (authorityOverride) body.authorityLevel = authorityOverride;
      const res = await authedFetch(`/api/sources/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        utils.source.getById.invalidate({ id: id! });
        toast.success("Classification updated");
      } else {
        toast.error("Failed to update classification");
      }
    } catch {
      toast.error("Failed to verify source");
    } finally {
      setVerifying(false);
    }
  }

  // Derived values
  const latestVersion = data?.versions?.length
    ? data.versions[data.versions.length - 1]
    : undefined;
  const latestVersionId = latestVersion?.id;

  // Cross-references
  const { data: references } = trpc.source.getReferences.useQuery(
    { sourceId: id! },
    { enabled: !!id },
  );

  // Common input for source/version-aware queries
  const sourceQueryInput = latestVersionId
    ? { sourceVersionId: latestVersionId }
    : { sourceId: id! };

  // Chunk count for metrics bar
  const { data: chunkCountData } = trpc.source.getChunks.useQuery(
    { ...sourceQueryInput, limit: 1, offset: 0 },
    { enabled: !!id },
  );

  // Page count
  const { data: pageCountData } = trpc.source.getPages.useQuery(
    { ...sourceQueryInput, limit: 1, offset: 0 },
    { enabled: !!id },
  );

  const hasBgeData =
    (pageCountData?.totalCount ?? 0) > 0 ||
    (chunkCountData?.totalCount ?? 0) > 0;

  if (isLoading) {
    return (
      <div className="space-y-8 px-10 py-10">
        {/* Back link */}
        <Skeleton className="h-4 w-16" />
        {/* Title */}
        <div className="space-y-3">
          <Skeleton className="h-9 w-96" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-6 w-40" />
        </div>
        {/* Tabs */}
        <Skeleton className="h-10 w-72 rounded-full" />
        {/* Content card */}
        <div className="bg-card rounded-lg max-w-3xl shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
          <div className="px-10 py-8 space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  const backToSourcesUrl = projectId
    ? `/projects/${projectId}/sources`
    : "/sources";

  if (!isValidId || error?.data?.code === "NOT_FOUND" || !data) {
    return (
      <EntityNotFound
        title="Source not found"
        entityName="source"
        backLink={{
          to: backToSourcesUrl,
          label: "Back to Sources",
        }}
      />
    );
  }

  if (error) {
    return (
      <EntityErrorState
        title="Failed to load source"
        onRetry={() => refetch()}
        backLink={{
          to: backToSourcesUrl,
          label: "Back to Sources",
        }}
      />
    );
  }

  const SourceTypeIcon =
    data.category === "design_system"
      ? Palette
      : (sourceTypeIcons[data.sourceType as keyof typeof sourceTypeIcons] ??
        FileText);
  const processingStep = (data.metadata as Record<string, unknown>)
    ?.processingStep as string | undefined;

  const backUrl = params.sourceId
    ? `/projects/${params.id}/sources`
    : "/sources";

  const pageActions = (
    <div className="flex items-center gap-2">
      {canEdit && data.sourceType === "manual" && (
        <Link to={`/manual-entry?sourceId=${data.id}`}>
          <Button variant="outline" size="sm">
            <PenLine className="size-3.5" />
            Edit
          </Button>
        </Link>
      )}
      {canEdit && data.sourceType === "web" && data.url && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleRetryCrawl}
          disabled={isCrawling}
        >
          {isCrawling ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <RefreshCw className="size-3.5" />
          )}
          Recrawl
        </Button>
      )}
      <CanEdit>
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Settings className="size-3.5" />
              Settings
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Source settings</DialogTitle>
              <DialogDescription>Configure {data.name}</DialogDescription>
            </DialogHeader>
            <SettingsTab
              data={data}
              onUpdate={(values) => {
                updateMutation.mutate({ id: id!, ...values });
                setSettingsOpen(false);
              }}
              onDelete={() => deleteMutation.mutate({ id: id! })}
              isDeleting={deleteMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      </CanEdit>
    </div>
  );

  return (
    <Page
      title={data.name}
      back={{ to: backUrl, label: "Sources" }}
      actions={pageActions}
    >
      <div className="space-y-6">
        {/* Source URL if available */}
        {data.url && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Globe className="size-3.5 shrink-0" />
            <span className="shrink-0">URL:</span>
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline truncate inline-flex items-center gap-1"
            >
              <span className="truncate">{data.url}</span>
              <ExternalLink className="size-3 shrink-0" />
            </a>
          </div>
        )}

        {/* Key Metrics / Specs Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-lg border border-border bg-card p-3.5 space-y-1">
            <span className="text-xs text-muted-foreground">Authority</span>
            <div className="flex items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded hover:bg-muted/60 p-0.5 -ml-0.5 text-left transition-colors"
                  >
                    <AuthorityBadge
                      level={verifyAuthority || data.authorityLevel}
                    />
                    <ChevronDown className="size-3 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {authorityLevelsSorted.map((a) => (
                    <DropdownMenuItem
                      key={a}
                      onSelect={() => {
                        setVerifyAuthority(a);
                        handleVerify(a);
                      }}
                    >
                      {authorityConfig[a].label}
                      {a === (verifyAuthority || data.authorityLevel) && (
                        <Check className="ml-auto size-3.5" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-3.5 space-y-1">
            <span className="text-xs text-muted-foreground">
              Active Snapshot
            </span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-semibold tabular-nums text-foreground">
                {latestVersion ? `v${latestVersion.versionNumber}` : "v1"}
              </span>
              <span className="text-xs text-muted-foreground">
                ({data.versions?.length ?? 1} total)
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-3.5 space-y-1">
            <span className="text-xs text-muted-foreground">
              Indexed Knowledge
            </span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-base font-semibold tabular-nums text-foreground">
                {chunkCountData?.totalCount ?? 0}
              </span>
              <span className="text-xs text-muted-foreground">
                {pageCountData && pageCountData.totalCount > 0
                  ? `${pageCountData.totalCount} pages`
                  : "chunks"}
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-3.5 space-y-1">
            <span className="text-xs text-muted-foreground">Sync State</span>
            <div className="flex items-center gap-1.5">
              <span
                className={`size-1.5 rounded-full shrink-0 ${statusDotColors[data.status] ?? "bg-muted-foreground"}`}
              />
              <span className="text-sm font-medium text-foreground capitalize">
                {statusLabels[data.status] ?? data.status}
              </span>
              {data.lastCrawledAt && (
                <span className="text-xs text-muted-foreground truncate">
                  · {formatRelativeTime(new Date(data.lastCrawledAt))}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Processing stepper */}
        {data.status === "processing" && !isCrawling && (
          <ProcessingStepper currentStep={processingStep} />
        )}

        {/* Crawl progress (from retry/recrawl SSE stream) */}
        {isCrawling && crawlProgress && (
          <Card>
            <CardContent className="px-6 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Crawling</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {crawlProgress.progress}%
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${crawlProgress.progress}%` }}
                />
              </div>
              {crawlProgress.currentTitle && (
                <p className="text-sm truncate">{crawlProgress.currentTitle}</p>
              )}
              {crawlProgress.currentUrl && (
                <p className="text-sm text-muted-foreground truncate">
                  {crawlProgress.currentUrl}
                </p>
              )}
              <div className="flex gap-4 text-xs text-muted-foreground">
                {crawlProgress.totalProcessed != null && (
                  <span>{crawlProgress.totalProcessed} pages</span>
                )}
                {crawlProgress.chunksCreated != null && (
                  <span>{crawlProgress.chunksCreated} chunks</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {pollingTimedOut && (
          <InlineWarning variant="caution">
            Processing is taking longer than expected. Refresh to check status.
          </InlineWarning>
        )}

        {/* Crawl schedule (if set) */}
        {data.crawlSchedule && (
          <div className="text-xs text-muted-foreground">
            Schedule: {getCronLabel(data.crawlSchedule) ?? data.crawlSchedule}
          </div>
        )}

        {/* Parse quality info (document sources) */}
        {data.sourceType === "document" && !!latestVersion?.metadata && (
          <ParseQualityInfo
            metadata={latestVersion.metadata as Record<string, unknown>}
          />
        )}

        {/* Crawl failure fallback */}
        {data.status === "error" &&
          (data.errorMessage?.includes("empty content") ||
            data.errorMessage?.includes("block")) && (
            <Card className="border-mark-important/40 bg-mark-important/8">
              <CardContent className="pt-4 space-y-3">
                <p className="text-sm font-medium text-severity-important">
                  Content could not be crawled
                </p>
                <p className="text-sm text-severity-important">
                  The automated crawler returned empty content.
                  {canEdit && " Try an alternative:"}
                </p>
                {canEdit && (
                  <ActionBar
                    align="start"
                    secondary={[
                      <Button key="paste" asChild>
                        <Link
                          to={`/manual-entry?sourceId=${data.id}&url=${encodeURIComponent(data.url ?? "")}&name=${encodeURIComponent(data.name)}&authorityLevel=${data.authorityLevel}`}
                        >
                          Paste content
                        </Link>
                      </Button>,
                      <Button key="upload" asChild>
                        <Link
                          to={`/upload?sourceId=${data.id}&name=${encodeURIComponent(data.name)}&authorityLevel=${data.authorityLevel}`}
                        >
                          Upload PDF
                        </Link>
                      </Button>,
                    ]}
                  />
                )}
              </CardContent>
            </Card>
          )}

        {/* Cross-References */}
        {references && references.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {references.map((ref) => (
              <Badge key={ref.id} variant="outline" className="text-xs">
                {ref.referenceType.replace("_", " ")}:&nbsp;
                {ref.targetSourceId && ref.targetSourceName ? (
                  <Link
                    to={`/sources/${ref.targetSourceId}`}
                    className="text-primary underline"
                  >
                    {ref.targetSourceName}
                  </Link>
                ) : (
                  ref.targetReference
                )}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* Tabs                                                              */}
      {/* ================================================================ */}
      <AriaTabs
        selectedKey={activeTab}
        onSelectionChange={(key) => {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set("tab", String(key));
              return next;
            },
            { replace: true },
          );
        }}
      >
        <AriaTabList aria-label="Source sections">
          <AriaTab id="content">Content</AriaTab>
          {hasBgeData && (
            <AriaTab id="pages">
              Pages
              {pageCountData && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({pageCountData.totalCount})
                </span>
              )}
            </AriaTab>
          )}
          <AriaTab id="knowledge">
            Chunks
            {chunkCountData && (
              <span className="ml-1 text-xs text-muted-foreground">
                ({chunkCountData.totalCount})
              </span>
            )}
          </AriaTab>
          {hasBgeData && data.projectId && (
            <AriaTab id="search">Search</AriaTab>
          )}
          <AriaTab id="history">History</AriaTab>
        </AriaTabList>

        <AriaTabPanel id="content">
          <ContentTab
            sourceVersionId={latestVersionId}
            sourceId={id}
            url={data.url}
          />
        </AriaTabPanel>

        {hasBgeData && (
          <AriaTabPanel id="pages">
            <PagesTab
              sourceVersionId={latestVersionId}
              sourceId={id}
              onSelectPage={(url) => {
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("tab", "knowledge");
                    next.set("pageUrl", url);
                    return next;
                  },
                  { replace: true },
                );
              }}
            />
          </AriaTabPanel>
        )}

        <AriaTabPanel id="knowledge">
          <KnowledgeTab
            sourceVersionId={latestVersionId}
            sourceId={id}
            pageUrl={searchParams.get("pageUrl") ?? undefined}
            onClearPageUrl={() => {
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete("pageUrl");
                  return next;
                },
                { replace: true },
              );
            }}
          />
        </AriaTabPanel>

        {hasBgeData && data.projectId && (
          <AriaTabPanel id="search">
            <ContentSearchTab projectId={data.projectId} />
          </AriaTabPanel>
        )}

        <AriaTabPanel id="history">
          <HistoryTab versions={data.versions ?? []} />
        </AriaTabPanel>
      </AriaTabs>
    </Page>
  );
}
